import React, { useState, useRef, useEffect } from 'react';
import './Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      text: "Hello! I'm here to help you with Green Circle's services. How can I assist you today?",
      sender: 'bot'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [messages, isOpen]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = {
      text: inputValue,
      sender: 'user'
    };

    setMessages([...messages, userMessage]);
    setInputValue('');

    // Simulate bot response
    setTimeout(() => {
      const botResponse = getBotResponse(inputValue);
      setMessages(prev => [...prev, { text: botResponse, sender: 'bot' }]);
    }, 500);
  };

  const getBotResponse = (userInput) => {
    const input = userInput.toLowerCase();

    // Greetings
    if (input.match(/hi|hello|hey|good morning|good afternoon|good evening/)) {
      return "Hello! Welcome to Green Circle. How can I help you today?";
    }

    // Services
    if (input.match(/service|what do you|what services|offer/)) {
      return "We offer various waste management services including:\n• Solid Waste Management\n• Biomedical Waste Management\n• Hazardous Waste Management\n• Faecal Sludge Management\n• Manpower Services\n• And much more! Would you like to know more about any specific service?";
    }

    // Contact
    if (input.match(/contact|phone|email|address|reach|location/)) {
      return "You can reach us through:\n• Visit our Contact Us page\n• Check our website for contact details\n• We're always here to help!";
    }

    // Waste management
    if (input.match(/waste|garbage|trash|collection|disposal/)) {
      return "We provide comprehensive waste management solutions including door-to-door collection, proper disposal, and recycling services. Which type of waste management are you interested in?";
    }

    // About
    if (input.match(/about|who are|company|organization/)) {
      return "Green Circle is a leading environmental services company committed to sustainable waste management and environmental protection. We serve communities and businesses with reliable and efficient services.";
    }

    // Pricing
    if (input.match(/price|cost|fee|charge|how much/)) {
      return "Our pricing depends on the service you need. Please contact us through our Contact Us page for a customized quote. We offer competitive rates!";
    }

    // Hours/Time
    if (input.match(/hours|time|when|schedule|available/)) {
      return "We operate during business hours. For specific scheduling and availability, please contact us directly through our Contact Us page.";
    }

    // Default responses
    if (input.match(/thank|thanks|appreciate/)) {
      return "You're welcome! Is there anything else I can help you with?";
    }

    if (input.match(/bye|goodbye|see you|farewell/)) {
      return "Thank you for contacting Green Circle! Have a great day!";
    }

    // Fallback
    return "I understand you're asking about: \"" + userInput + "\". For detailed information, please visit our website or contact us through our Contact Us page. How else can I assist you?";
  };

  return (
    <>
      {/* Chatbot Button */}
      <div 
        className={`chatbot-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className={`fas ${isOpen ? 'fa-times' : 'fa-comments'}`}></i>
        {!isOpen && <span className="chatbot-notification">1</span>}
      </div>

      {/* Chatbot Window */}
      {isOpen && (
        <div className="chatbot-container">
          <div className="chatbot-header">
            <div className="chatbot-header-content">
              <div className="chatbot-avatar">
                <i className="fas fa-robot"></i>
              </div>
              <div className="chatbot-header-text">
                <h3>Green Circle Assistant</h3>
                <p>We're here to help</p>
              </div>
            </div>
            <button 
              className="chatbot-close"
              onClick={() => setIsOpen(false)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`chatbot-message ${msg.sender}`}>
                <div className="message-content">
                  {msg.text.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < msg.text.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form className="chatbot-input-form" onSubmit={handleSend}>
            <input
              ref={inputRef}
              type="text"
              className="chatbot-input"
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <button type="submit" className="chatbot-send">
              <i className="fas fa-paper-plane"></i>
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default Chatbot;
